import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";

const urlB64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
};

// Branded opt-in card for appointment reminders & weekly journal essays.
// Shown only when the backoffice "Push Notifications" toggle is on.
export const PushOptIn = () => {
  const { content } = useContent();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (content?.settings?.push_enabled !== "on") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || typeof Notification === "undefined") return;
    // preview override: /?pushdemo=1 force-shows the card (e.g. for demos)
    const force = new URLSearchParams(window.location.search).has("pushdemo");
    if (!force && Notification.permission !== "default") return;
    try {
      if (!force && sessionStorage.getItem("rs_push_snooze")) return;
    } catch {}
    const t = setTimeout(() => setVisible(true), force ? 800 : 7000);
    return () => clearTimeout(t);
  }, [content]);

  const snooze = () => {
    try {
      sessionStorage.setItem("rs_push_snooze", "1");
    } catch {}
    setVisible(false);
  };

  const enable = async () => {
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        snooze();
        return;
      }
      const reg = await navigator.serviceWorker.register("/sw.js");
      const { data } = await api.get("/push/public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(data.publicKey),
      });
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      setVisible(false);
      toast.success("You're on the list — your welcome gift is on its way.");
    } catch {
      toast.error("Couldn't enable notifications just now — please try again later.");
      snooze();
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.97 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-24 right-5 z-[85] w-[min(92vw,340px)]"
          data-testid="push-optin-card"
          role="dialog"
          aria-label="Enable notifications"
        >
          <div className="overflow-hidden rounded-[6px] border border-[#D4AF37]/30 bg-[#1A1615]/95 p-6 shadow-[0_24px_70px_rgba(26,22,21,0.5)] backdrop-blur-xl">
            <button
              onClick={snooze}
              aria-label="Not now"
              data-testid="push-optin-dismiss"
              className="absolute right-3 top-3 text-[#A89B8C] transition-colors hover:text-[#F5EFE6]"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
            <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/40">
              <Bell className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" />
            </div>
            <h3 className="mt-4 font-display text-xl font-normal text-[#F5EFE6]">Never miss a glow-up</h3>
            <p className="mt-2 text-xs font-light leading-relaxed text-[#A89B8C]">
              Gentle appointment reminders and Dr. Javeria's weekly skin essays — plus a welcome gift the moment you
              join. No noise, we promise.
            </p>
            <div className="mt-5 flex gap-3">
              <button onClick={enable} data-testid="push-optin-enable" className="btn-gold flex-1 px-4 py-2.5 text-xs">
                Enable Notifications
              </button>
              <button
                onClick={snooze}
                data-testid="push-optin-later"
                className="px-4 py-2.5 text-xs font-medium text-[#A89B8C] transition-colors hover:text-[#F5EFE6]"
              >
                Not now
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
